import React from 'react'
import { Nullable } from '../../common/Nullable'
import { FilePart } from './common/database'
import { Log } from '../../common/logger'
import { RoomSession } from '../../models/Room'
import { RecordingStatus } from './hooks/useNativeMediaRecorder'

export interface InPersonFileApp {
  getAudioChunks: (accountId: string, roomId: string, sessionId: string) => Promise<FilePart[]>
  deleteAudioChunks: (accountId: string, roomId: string, sessionId: string) => Promise<void>
  uploadAudioChunks: (accountId: string, roomId: string, sessionId: string, fileName: string, sequence: number, blobs: Blob[]) => Promise<void>
  uploadLogFile: (accountId: string, roomId: string, sessionId: string, fileName: string, logs: Log[]) => Promise<void>
}

export const BaseAppContext = React.createContext<Nullable<InPersonFileApp>>(null)

export interface InPersonMeetingApp {
  isOnline: boolean
  micPermissionError: [Nullable<string>, Nullable<string>]
  wakeLockStatus: boolean
  toggleWakeLock: () => void
  audioInputDevices: Nullable<Array<MediaDeviceInfo>>
  changeAudioDeviceId: (deviceId: string) => void
  currentAudioDeviceId: Nullable<string>
  currentSession: Nullable<RoomSession>
  setCurrentSession: (session: Nullable<RoomSession>) => void
  createAudioChunk: (filepart: FilePart) => Promise<void>
  mediaRecorder: {
    createRecorder: (stream: MediaStream) => Promise<MediaRecorder | null>
    startRecording: (timeslice?: number | undefined) => Promise<void>
    stopRecording: () => void
    pauseRecording: () => void
    resumeRecording: () => void
    recordingStatus: RecordingStatus
    requestData: () => void
  }
  getBrowserSpec: () => Promise<{ totalSpace: number | undefined; usedSpace: number | undefined } | undefined>
  mediaDevicesLoading: boolean
  playComplianceAudio: (sessionId?: string) => Promise<void>
}

export const AppContext = React.createContext<Nullable<InPersonMeetingApp>>(null)

type VolumeContextType = {
  volume: number
  setVolume: React.Dispatch<React.SetStateAction<number>>
}

export const VolumeContext = React.createContext<VolumeContextType>({
  volume: 0,
  setVolume: () => {},
})

export const useInPersonMeetingFileApp = () => {
  const ctx = React.useContext(BaseAppContext)
  if (!ctx) throw new Error('Unable to initialize BaseAppContext')
  return ctx
}

export const useInPersonMeetingApp = () => {
  const ctx = React.useContext(AppContext)
  if (!ctx) throw new Error('Unable to initialize AppContext')
  return ctx
}

export const useVolume = () => {
  return React.useContext(VolumeContext)
}
